import { Activity, ArrowRight, Heart, MessageSquareText, Moon, Play, Sparkles } from 'lucide-react';
import { Logo } from './Logo';

interface LandingPageProps {
  onShowLogin: () => void;
  onTryDemo: () => void;
}

export function LandingPage({ onShowLogin, onTryDemo }: LandingPageProps) {
  const handleShowAuth = (isSignUp: boolean) => {
    // Store the intended auth mode in sessionStorage
    sessionStorage.setItem('authMode', isSignUp ? 'signup' : 'login');
    onShowLogin();
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Navigation */}
      <nav className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Logo />
        <div className="flex items-center gap-2">
          <button onClick={() => handleShowAuth(false)} className="btn-ghost">
            Log in
          </button>
          <button onClick={() => handleShowAuth(true)} className="btn-primary">
            Get started
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pb-20 pt-16 lg:pt-24">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-600/10 px-3 py-1 text-xs font-semibold text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300">
              <Sparkles className="h-3.5 w-3.5" />
              Personalized daily training guidance
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.1] tracking-tight text-zinc-900 dark:text-white sm:text-5xl lg:text-6xl">
              Your body writes the story.
              <span className="block bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
                FitScript tells you what comes next.
              </span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-zinc-600 dark:text-zinc-400">
              Every morning, FitScript reads your sleep and recovery data from Oura
              and your training history from Strava, then recommends the one workout
              your body is actually ready for. No guesswork — just the right session,
              at the right intensity, with the reasoning to back it up.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={() => handleShowAuth(true)} className="btn-primary px-6 py-3 text-base">
                Get started free
                <ArrowRight className="h-4 w-4" />
              </button>
              <button onClick={onTryDemo} className="btn-secondary px-6 py-3 text-base">
                <Play className="h-4 w-4" />
                Try a sample day
              </button>
            </div>
            <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
              No signup — explore the dashboard and talk to the live AI coach.
            </p>
          </div>

          {/* Product preview */}
          <div className="animate-fade-up-delay-1">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">
                  Today&apos;s recommendation
                </p>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Ready to train
                </span>
              </div>
              <h3 className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white">
                Zone 2 Endurance Ride
              </h3>
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                45 minutes · moderate intensity
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                  <div className="flex items-center gap-1.5 text-violet-600 dark:text-violet-400">
                    <Moon className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">Sleep</span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-zinc-900 dark:text-white">86%</p>
                </div>
                <div className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                  <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <Activity className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">HRV</span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-zinc-900 dark:text-white">52ms</p>
                </div>
                <div className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-950/5 dark:bg-zinc-800/60 dark:ring-white/5">
                  <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
                    <Heart className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium">RHR</span>
                  </div>
                  <p className="mt-1 text-lg font-bold tabular-nums text-zinc-900 dark:text-white">49</p>
                </div>
              </div>
              <div className="mt-5 rounded-xl bg-indigo-600/5 p-4 text-sm leading-relaxed text-zinc-600 ring-1 ring-indigo-600/10 dark:bg-indigo-400/5 dark:text-zinc-400 dark:ring-indigo-400/10">
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">Why this workout?</span>{' '}
                Strong sleep and rising HRV after yesterday&apos;s intervals — your body
                can absorb aerobic volume today without cutting into recovery.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-zinc-950/5 bg-white py-20 dark:border-white/5 dark:bg-zinc-900/40">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
            How it works
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-zinc-600 dark:text-zinc-400">
            Three steps between you and training that actually fits your recovery.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600/10 text-violet-600 dark:bg-violet-400/10 dark:text-violet-400">
                <Moon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Connect your data
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                Link Oura for sleep, HRV, and readiness, and Strava for your complete
                training history. Read-only, revocable anytime.
              </p>
            </div>
            <div className="card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/10 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400">
                <Activity className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                We read your readiness
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                Every morning FitScript weighs sleep quality, heart-rate variability,
                and recent training load into a single readiness picture.
              </p>
            </div>
            <div className="card p-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-400">
                <MessageSquareText className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Get today&apos;s workout
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                One clear recommendation matched to your goals — with low- and
                high-energy alternatives and the reasoning behind it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + footer */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
            Ready to train smarter?
          </h2>
          <p className="mt-3 text-zinc-600 dark:text-zinc-400">
            Whether you&apos;re chasing a race, building strength, or just trying to feel
            like yourself again — FitScript meets you where you are.
          </p>
          <button onClick={() => handleShowAuth(true)} className="btn-primary mt-8 px-8 py-3 text-base">
            Get started free
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-zinc-950/5 py-8 dark:border-white/5">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
          <Logo />
          <div className="flex gap-6 text-sm text-zinc-500 dark:text-zinc-400">
            <a href="/privacy" className="hover:text-zinc-900 dark:hover:text-white">Privacy</a>
            <a href="/terms" className="hover:text-zinc-900 dark:hover:text-white">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
